import React, { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  Globe,
  Flag,
  MoreHorizontal,
} from "lucide-react";
import { Card } from "../ui/card";
import { ClickableMetric } from "../clickable-metric";
import { MetricTerm } from "../metric-term";
import { MetricInsightChips } from "../metric-insight-chips";
import { TooltipIcon } from "../tooltip-icon";
import {
  competitivePositionInsights,
  competitiveVelocityInsights,
  metricExplanations,
} from "../../data/antenna-metric-explanations";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CompetitivePositionItem {
  rank: number;
  brand: string;
  score: number;
  points: number;
  category: string;
  color: string;
  country?: string;
  region?: string;
  rankVerified?: boolean;
  rankSource?: string;
}

interface CompetitiveScopeRanks {
  globalRank: number;
  globalRankVerified?: boolean;
  globalRankSource?: string;
  localRank: number;
  localRankVerified?: boolean;
  localRankSource?: string;
  brandCountry: string;
  brandRegion: string;
}

interface AntennaCompetitiveIntelligenceProps {
  competitivePosition: CompetitivePositionItem[];
  competitivePositionLocal?: CompetitivePositionItem[];
  competitiveScopeRanks?: CompetitiveScopeRanks;
  competitiveVelocity: any;
  brandName: string;
  isExpanded: boolean;
  toggleExpanded: () => void;
  onOpenAIChat: (msg: string) => void;
}

type ScopeTab = "global" | "local";

// ─── Verified / unverified badge ─────────────────────────────────────────────

function VerifiedBadge({ verified, source }: { verified?: boolean; source?: string }) {
  const label   = verified ? "Verified"   : "AI estimate";
  const color   = verified ? "#2E7D32"    : "#F57C00";
  const bg      = verified ? "#2E7D3212"  : "#F57C0012";
  const border  = verified ? "#2E7D3240"  : "#F57C0040";
  const icon    = verified ? "✓"          : "~";
  const tooltip = source   ? `${label} · ${source}` : label;

  return (
    <span
      title={tooltip}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        padding: "1px 5px",
        borderRadius: 4,
        backgroundColor: bg,
        border: `1px solid ${border}`,
        color,
        fontSize: "10px",
        fontWeight: 600,
        flexShrink: 0,
        cursor: "default",
        whiteSpace: "nowrap" as const,
      }}
    >
      {icon} {label}
    </span>
  );
}

// ─── Adaptive rank indicator ──────────────────────────────────────────────────
// Circle for 1–2 digit ranks, rounded pill for 3+ digits so large ranks
// (#1847, #12345) never overflow or clip. Same color tokens as before.

function RankPill({
  rank,
  color,
  isYou,
}: {
  rank: number;
  color: string;
  isYou: boolean;
}) {
  const rankStr = String(rank);
  const isLong = rankStr.length >= 3;

  const borderColor = isYou ? "var(--alpha-teal-40)" : `${color}40`;
  const bgColor = isYou ? "var(--alpha-teal-15)" : `${color}15`;
  const textColor = isYou ? "var(--alpha-teal)" : color;

  if (!isLong) {
    return (
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: 32,
          height: 32,
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          color: textColor,
          fontSize: "var(--text-xs)",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums" as const,
        }}
      >
        #{rank}
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        height: 32,
        minWidth: 32,
        padding: "0 10px",
        borderRadius: 999,
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        color: textColor,
        fontSize: "var(--text-xs)",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums" as const,
        whiteSpace: "nowrap" as const,
      }}
    >
      #{rank}
    </div>
  );
}

// ─── Brand matching helpers ───────────────────────────────────────────────────

const CORP_SUFFIXES =
  /\b(inc|incorporated|llc|ltd|limited|corp|corporation|co|company|plc|sa|ag|gmbh|srl|bv|pvt|pte|group)\b/gi;

function stripBrandPunct(s: string): string {
  return (s || "")
    .toLowerCase()
    .replace(/[.,(){}\[\]:;|/]/g, " ")
    .replace(/[-–—]/g, " ")
    .replace(CORP_SUFFIXES, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Words too generic to establish that two names are the same brand on their
// own. Without this guard, the old first-token heuristic marked EVERY
// "Saint X" school as "You" on a "Saint Y" customer's ranking (Saint Leo the
// Great badged as Saint Mary).
const GENERIC_BRAND_TOKENS = new Set([
  "the", "of", "and", "a", "an", "at", "in",
  "saint", "st", "holy", "our", "lady", "mount", "mt",
  "new", "san", "santa", "la", "el",
  "school", "academy", "prep", "preparatory", "college", "university",
  "institute", "center", "centre",
]);

function isSameBrand(a: string, b: string): boolean {
  if (!a || !b) return false;
  const na = stripBrandPunct(a);
  const nb = stripBrandPunct(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length >= 3 && nb.length >= 3) {
    if (na.includes(nb) || nb.includes(na)) return true;
  }
  // Token-subset match: every token of the shorter name must appear in the
  // longer name, and at least one shared token must be distinctive (not in
  // GENERIC_BRAND_TOKENS). Keeps "Saint Mary School" matching "Saint Mary
  // Catholic Classical School" while never conflating two different
  // "Saint X" schools.
  const ta = new Set(na.split(" "));
  const tb = new Set(nb.split(" "));
  const [small, large] = ta.size <= tb.size ? [ta, tb] : [tb, ta];
  let distinctive = false;
  for (const t of small) {
    if (!large.has(t)) return false;
    if (!GENERIC_BRAND_TOKENS.has(t)) distinctive = true;
  }
  return distinctive;
}

// ─── Gap row ─────────────────────────────────────────────────────────────────
// Rendered between ranked-list rows when there's a significant jump (e.g. from
// #3 to #47). Without this the user sees an abrupt jump in numbers and can
// reasonably wonder if data is missing. The gap row makes the jump explicit.

function GapRow({ count }: { count: number }) {
  return (
    <div
      className="flex items-center justify-center gap-2 py-2"
      style={{
        backgroundColor: "transparent",
      }}
    >
      <MoreHorizontal size={14} style={{ color: "var(--muted-foreground)" }} />
      <span
        style={{
          fontSize: "11px",
          color: "var(--muted-foreground)",
          fontStyle: "italic" as const,
        }}
      >
        {count} brand{count === 1 ? "" : "s"} in between
      </span>
      <MoreHorizontal size={14} style={{ color: "var(--muted-foreground)" }} />
    </div>
  );
}

// ─── Full ranked list ─────────────────────────────────────────────────────────

// Show the "N brands in between" row for ANY skipped rank, not only big
// jumps. The ranked list only contains brands the model actually named, so
// rank numbering can hop (24 then 27); an unexplained hop reads as arbitrary
// numbering. Threshold 1 = consecutive ranks show nothing, any skip explains
// itself.
const GAP_THRESHOLD = 1;

function RankedList({
  items,
  brandName,
}: {
  items: CompetitivePositionItem[];
  brandName: string;
}) {
  const sorted = [...items].filter((i) => i?.brand).sort((a, b) => a.rank - b.rank);
  if (sorted.length === 0) return null;

  return (
    <div className="space-y-3">
      {sorted.map((item, idx) => {
        const isYou = isSameBrand(item.brand, brandName);

        const locationTag = item.country
          ? `${item.country} · ${item.region || "Region cannot be identified"}`
          : null;

        // Show a gap indicator if the rank jumped meaningfully from the
        // previous row. Threshold of >5 means consecutive 1,2,3,5,8 reads
        // cleanly but 3 → 47 gets called out explicitly.
        const prevItem = idx > 0 ? sorted[idx - 1] : null;
        const gap = prevItem ? item.rank - prevItem.rank - 1 : 0;
        const showGap = prevItem && (item.rank - prevItem.rank) > GAP_THRESHOLD;

        return (
          <React.Fragment key={`${item.brand}-${idx}`}>
            {showGap && <GapRow count={gap} />}

            <div
              className="flex items-center gap-4 p-3 rounded-lg transition-all hover-scale"
              style={{
                backgroundColor: isYou ? "var(--alpha-teal-10)" : "var(--muted-10)",
                border: isYou ? "2px solid var(--alpha-teal)" : "1px solid var(--border)",
                boxShadow: isYou ? "0 0 0 3px var(--alpha-teal-10)" : undefined,
                position: "relative",
                minHeight: 60,
                boxSizing: "border-box",
              }}
            >
              <RankPill rank={item.rank} color={item.color} isYou={isYou} />

              {/* Left: brand + category, given room to breathe */}
              <div className="flex-1 min-w-0">
                <span
                  className="text-sm font-medium"
                  style={{ color: isYou ? "var(--alpha-teal)" : undefined }}
                >
                  {item.brand}
                  {isYou && (
                    <span className="ml-1 text-xs font-bold" style={{ color: "var(--alpha-teal)" }}>
                      ★ You
                    </span>
                  )}
                </span>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginTop: 2 }}>
                  {item.category}
                </div>
              </div>

              {/* Right: metadata moved over to balance the row (replaces the score) */}
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {item.rankVerified && (
                  <VerifiedBadge verified source={item.rankSource ?? undefined} />
                )}
                {locationTag && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: "var(--muted)",
                      border: "1px solid var(--border)",
                      fontSize: "10px",
                      color: item.region
                        ? "var(--muted-foreground)"
                        : "var(--warning-text)",
                      whiteSpace: "nowrap" as const,
                    }}
                  >
                    <Flag size={9} />
                    {locationTag}
                  </span>
                )}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Velocity status badge ────────────────────────────────────────────────────

function VelocityStatusBadge({ status }: { status: any }) {
  // status is a 0-100 scale where 50 is neutral: above 50 means the
  // competitors AI tracks near you are net gaining ground, below 50 means
  // they're net losing it. When no status has been measured, say so honestly
  // instead of fabricating a "Stable" reading.
  if (status == null || Number.isNaN(Number(status))) {
    return (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg"
        style={{
          backgroundColor: "var(--muted)",
          border: "1px solid var(--border)",
          color: "var(--muted-foreground)",
        }}
      >
        <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Not measured yet</span>
      </div>
    );
  }
  const val = Number(status);
  let label: string;
  let color: string;
  let Icon: React.ElementType;

  // This badge reads the MARKET's temperature in the models' eyes (net
  // movement across the competitive set), not a good/bad score for the
  // brand. Colors follow temperature: warm amber for a heating market,
  // calm blue for stable, cool gray for a cooling one. Deliberately no
  // green anywhere so no state reads as "good news" by default.
  if (val >= 65) {
    label = "Rising";
    color = "#f59e0b";
    Icon = TrendingUp;
  } else if (val > 35) {
    label = "Stable";
    color = "#28a9d2";
    Icon = Minus;
  } else {
    label = "Declining";
    color = "#94a3b8";
    Icon = TrendingDown;
  }

  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg"
      style={{
        backgroundColor: `${color}15`,
        border: `1px solid ${color}40`,
        color,
      }}
    >
      <Icon size={14} />
      <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>{label}</span>
    </div>
  );
}

// ─── Velocity competitor row ──────────────────────────────────────────────────

function VelocityRow({
  item,
  direction,
  onOpenAIChat,
}: {
  item: any;
  direction: "rising" | "declining";
  onOpenAIChat: (msg: string) => void;
}) {
  const isRising   = direction === "rising";
  const accentColor = isRising ? "var(--signal-green)" : "var(--infrared-coral)";
  const valueStr   = isRising ? `+${item.change}%` : `${item.change}%`;

  return (
    <div
      className="flex items-center justify-between px-5 py-3 rounded-lg transition-all hover-scale"
      style={{
        backgroundColor: "var(--muted-10)",
        border: "1px solid var(--border)",
        borderLeft: `3px solid ${accentColor}`,
        minHeight: 60,
        boxSizing: "border-box",
      }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm font-medium truncate">{item.fullName}</span>
        {item.country && (
          <span
            className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: "var(--muted)",
              border: "1px solid var(--border)",
              fontSize: "10px",
              color: "var(--muted-foreground)",
            }}
          >
            <Flag size={9} />
            {item.country}
          </span>
        )}
      </div>
      <ClickableMetric
        value={valueStr}
        label={isRising ? "Growth" : "Decline"}
        calculation={isRising ? "Rising momentum" : "Decreasing momentum"}
        onClick={() => onOpenAIChat(`${item.fullName} ${direction}`)}
        style={{
          color: accentColor,
          fontWeight: 600,
          fontSize: "var(--text-sm)",
          fontVariantNumeric: "tabular-nums",
          flexShrink: 0,
        }}
      />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const VELOCITY_PREVIEW = 3;

export function AntennaCompetitiveIntelligence({
  competitivePosition,
  competitivePositionLocal = [],
  competitiveScopeRanks,
  competitiveVelocity,
  brandName,
  isExpanded,
  toggleExpanded,
  onOpenAIChat,
}: AntennaCompetitiveIntelligenceProps) {
  const [scopeChoice,  setScopeChoice]  = useState<ScopeTab | null>(null);
  const [risingOpen,   setRisingOpen]   = useState(false);
  const [decliningOpen, setDecliningOpen] = useState(false);

  const scopeRanks: CompetitiveScopeRanks = competitiveScopeRanks ?? {
    globalRank: 0,
    localRank: 0,
    brandCountry: "",
    brandRegion: "",
  };

  // ── Adaptive scope tabs ──────────────────────────────────────────────────
  // Scopes are derived from the data instead of hardcoded "Global + country".
  // Each list is labeled by its own geographic span: items across multiple
  // countries -> Global; one country, multiple regions -> that country; one
  // region -> that region. A local brand never sees a meaningless "Global"
  // rank, and when both lists cover the SAME market (a Monmouth County
  // school whose "global" list is all New Jersey schools), the toggle
  // collapses to a single honestly-labeled list.
  const scopeConfig = useMemo(() => {
    const describe = (items: CompetitivePositionItem[]) => {
      const countries = new Set(
        items.map((i) => (i.country || "").trim().toLowerCase()).filter(Boolean),
      );
      if (countries.size > 1) {
        return { label: "Global", icon: Globe as React.ElementType, span: "global" };
      }
      const regions = new Set(
        items.map((i) => (i.region || "").trim().toLowerCase()).filter(Boolean),
      );
      const country =
        items.find((i) => (i.country || "").trim())?.country?.trim() ||
        (scopeRanks.brandCountry || "").trim();
      if (regions.size > 1) {
        return { label: country || "Global", icon: Flag as React.ElementType, span: country ? "country" : "global" };
      }
      const region =
        items.find((i) => (i.region || "").trim())?.region?.trim() ||
        (scopeRanks.brandRegion || "").trim();
      const label = region || country || "Global";
      return {
        label,
        icon: (label === "Global" ? Globe : Flag) as React.ElementType,
        span: region ? "region" : country ? "country" : "global",
      };
    };

    const globalList = competitivePosition || [];
    const localList  = competitivePositionLocal || [];
    const globalDesc = describe(globalList);
    const localDesc  = describe(localList);

    const tabs: { key: ScopeTab; label: string; icon: React.ElementType; rank: number; span: string }[] = [];
    const sameMarket =
      globalList.length > 0 && localList.length > 0 &&
      globalDesc.label.toLowerCase() === localDesc.label.toLowerCase();

    if (sameMarket) {
      // One market, one list: keep the richer of the two.
      const keepGlobal = globalList.length >= localList.length;
      tabs.push({
        key: keepGlobal ? "global" : "local",
        label: globalDesc.label,
        icon: globalDesc.icon,
        rank: keepGlobal ? scopeRanks.globalRank : scopeRanks.localRank,
        span: globalDesc.span,
      });
    } else {
      if (globalList.length > 0) {
        tabs.push({ key: "global", label: globalDesc.label, icon: globalDesc.icon, rank: scopeRanks.globalRank, span: globalDesc.span });
      }
      if (localList.length > 0) {
        tabs.push({ key: "local", label: localDesc.label, icon: localDesc.icon, rank: scopeRanks.localRank, span: localDesc.span });
      }
    }
    if (tabs.length === 0) {
      tabs.push({ key: "global", label: "Global", icon: Globe as React.ElementType, rank: scopeRanks.globalRank, span: "global" });
    }
    // Home market first: default to the narrowest scope shown.
    return { tabs, defaultKey: tabs[tabs.length - 1].key };
  }, [competitivePosition, competitivePositionLocal, scopeRanks.globalRank, scopeRanks.localRank, scopeRanks.brandCountry, scopeRanks.brandRegion]);

  const activeScope: ScopeTab =
    scopeChoice && scopeConfig.tabs.some((t) => t.key === scopeChoice)
      ? scopeChoice
      : scopeConfig.defaultKey;

  const activeItems = useMemo(() => {
    if (activeScope === "local") return competitivePositionLocal;
    return competitivePosition;
  }, [activeScope, competitivePosition, competitivePositionLocal]);

  // Reconcile category-relative rank vs. absolute scope rank for the user's brand
  const sortedActive = useMemo(() => {
    const correctScopeRank = activeScope === "local"
      ? scopeRanks.localRank
      : scopeRanks.globalRank;

    return [...activeItems]
      .filter((i) => i?.brand)
      .map((item) => {
        if (correctScopeRank > 0 && isSameBrand(item.brand, brandName)) {
          return { ...item, rank: correctScopeRank };
        }
        return item;
      })
      .sort((a, b) => a.rank - b.rank);
  }, [activeItems, activeScope, scopeRanks.globalRank, scopeRanks.localRank, brandName]);

  const topRisingCompetitor = competitiveVelocity?.rising?.[0];

  const risingItems   = competitiveVelocity?.rising   || [];
  const decliningItems = competitiveVelocity?.declining || [];
  const risingOverflow   = risingItems.length   - VELOCITY_PREVIEW;
  const decliningOverflow = decliningItems.length - VELOCITY_PREVIEW;
  const visibleRising    = risingOpen    ? risingItems   : risingItems.slice(0, VELOCITY_PREVIEW);
  const visibleDeclining = decliningOpen ? decliningItems : decliningItems.slice(0, VELOCITY_PREVIEW);

  const activeTab =
    scopeConfig.tabs.find((t) => t.key === activeScope) ?? scopeConfig.tabs[0];
  const activeScopeDescription =
    activeTab.span === "global"
      ? "Ranked by AI recall strength globally"
      : `Ranked by AI recall strength in ${activeTab.label}`;

  const scopeTabs = scopeConfig.tabs;

  return (
    <>
      <Card style={{ backgroundColor: "var(--card)", borderColor: "var(--border)" }}>
        <div className="p-6 space-y-6">

          {/* ── Header ──────────────────────────────────────────────────── */}
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={toggleExpanded}
            style={{ userSelect: "none" }}
          >
            <div>
              <h3 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)" }}>
                <MetricTerm
                  {...metricExplanations["Competitive Intelligence Score"]}
                  definition="Your category ranking across AI platforms"
                  onOpenAIChat={() => onOpenAIChat("Competitive Intelligence Score")}
                >
                  Competitive Intelligence
                </MetricTerm>
              </h3>
              <p style={{ color: "var(--muted-foreground)", marginTop: "4px", fontSize: "var(--text-sm)" }}>
                Where do you rank in model awareness of the competitive landscape?
              </p>
            </div>
            {isExpanded
              ? <ChevronUp   className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
              : <ChevronDown className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />}
          </div>

          {isExpanded && (
            <div className="@container space-y-6">

              {/* ── Two-column: Position + Velocity ───────────────────── */}
              <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-8 @3xl:gap-12 items-stretch">

                {/* Left: Competitive Position */}
                <div className="flex flex-col gap-4 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h3 style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)" }}>
                        Competitive Position
                      </h3>
                      <p style={{ color: "var(--muted-foreground)", marginTop: "4px", fontSize: "var(--text-sm)" }}>
                        {activeScopeDescription}
                      </p>
                    </div>

                    {/* ── Scope tabs with rank prominently in button ───── */}
                    <div className="flex gap-2 flex-wrap flex-shrink-0">
                      {scopeTabs.map((tab) => {
                        const isActive = activeScope === tab.key;
                        const isChoice = scopeTabs.length > 1;
                        return (
                          <button
                            key={tab.key}
                            onClick={isChoice ? () => setScopeChoice(tab.key) : undefined}
                            className="flex items-center gap-2 px-3 py-2 rounded-md transition-all"
                            style={{
                              backgroundColor: isActive ? "var(--accent)" : "var(--muted)",
                              color: isActive ? "var(--accent-foreground)" : "var(--muted-foreground)",
                              border: isActive ? "1px solid var(--accent)" : "1px solid var(--border)",
                              fontSize: "var(--text-xs)",
                              fontWeight: isActive ? 600 : 400,
                              cursor: isChoice ? "pointer" : "default",
                            }}
                          >
                            <tab.icon size={12} />
                            {tab.label}
                            {tab.rank > 0 && (
                              <span
                                className="px-1.5 py-0.5 rounded"
                                style={{
                                  backgroundColor: isActive ? "rgba(255,255,255,0.2)" : "var(--background)",
                                  fontSize: "11px",
                                  fontWeight: 700,
                                }}
                              >
                                #{tab.rank}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {sortedActive.length === 0 ? (
                    <div
                      className="p-6 rounded-xl border text-center"
                      style={{ backgroundColor: "var(--muted-10)", borderColor: "var(--border)" }}
                    >
                      <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                        No data available for this scope yet.
                      </p>
                    </div>
                  ) : (
                    <RankedList items={sortedActive} brandName={brandName} />
                  )}

                  <div className="mt-auto">
                    <MetricInsightChips
                      insights={competitivePositionInsights}
                      onOpenAIChat={() => onOpenAIChat("Competitive Position insights")}
                      maxChips={2}
                    />
                  </div>
                </div>

                {/* Right: Competitive Velocity */}
                <div className="flex flex-col gap-4 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h3 style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)" }}>
                        <MetricTerm
                          {...metricExplanations["Competitive Velocity"]}
                          onOpenAIChat={() => onOpenAIChat("Competitive Velocity")}
                        >
                          Competitive Velocity
                        </MetricTerm>
                      </h3>
                      <p style={{ color: "var(--muted-foreground)", marginTop: "4px", fontSize: "var(--text-sm)" }}>
                        Market momentum and trend changes
                      </p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                        Overall market status:
                      </span>
                      <VelocityStatusBadge status={competitiveVelocity?.status} />
                    </div>
                  </div>

                  {/* Honest empty state: no fabricated movement before data exists */}
                  {risingItems.length === 0 && decliningItems.length === 0 && (
                    <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", lineHeight: 1.5 }}>
                      No competitor movement measured yet. Velocity appears once there is enough measurement history to compare.
                    </p>
                  )}

                  {/* Rising */}
                  {risingItems.length > 0 && (
                    <div>
                      <h4
                        className="mb-2"
                        style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em" }}
                      >
                        Rising Competitors
                      </h4>
                      <div className="space-y-3">
                        {visibleRising.map((item: any, index: number) => (
                          <VelocityRow key={index} item={item} direction="rising" onOpenAIChat={onOpenAIChat} />
                        ))}
                      </div>
                      {risingOverflow > 0 && (
                        <button
                          onClick={() => setRisingOpen(!risingOpen)}
                          className="flex items-center gap-1 mt-2"
                          style={{ fontSize: "var(--text-xs)", color: "#28a9d2", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                        >
                          {risingOpen
                            ? <><ChevronUp className="w-3 h-3" /> Show less</>
                            : <><ChevronDown className="w-3 h-3" /> {risingOverflow} more</>}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Declining */}
                  {decliningItems.length > 0 && (
                    <div>
                      <h4
                        className="mb-2"
                        style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em" }}
                      >
                        Declining Competitors
                      </h4>
                      <div className="space-y-3">
                        {visibleDeclining.map((item: any, index: number) => (
                          <VelocityRow key={index} item={item} direction="declining" onOpenAIChat={onOpenAIChat} />
                        ))}
                      </div>
                      {decliningOverflow > 0 && (
                        <button
                          onClick={() => setDecliningOpen(!decliningOpen)}
                          className="flex items-center gap-1 mt-2"
                          style={{ fontSize: "var(--text-xs)", color: "#28a9d2", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                        >
                          {decliningOpen
                            ? <><ChevronUp className="w-3 h-3" /> Show less</>
                            : <><ChevronDown className="w-3 h-3" /> {decliningOverflow} more</>}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="mt-auto">
                    <MetricInsightChips
                      insights={competitiveVelocityInsights}
                      onOpenAIChat={() => onOpenAIChat("Competitive Velocity insights")}
                      maxChips={2}
                    />
                  </div>
                </div>
              </div>
              {/* ── Competitive alert — same card, distinct tint ──────── */}
              {topRisingCompetitor && (
                <>
                  <div style={{ borderTop: "1px solid var(--border)" }} />
                  <div
                    className="rounded-xl p-4"
                    style={{
                      backgroundColor: "var(--alpha-coral-10, rgba(255,109,102,0.06))",
                      border:          "1px solid var(--border)",
                      borderLeft:      "3px solid var(--infrared-coral)",
                    }}
                  >
                    <div className="flex items-start gap-4">
                      <TooltipIcon
                        Icon={AlertCircle}
                        tooltip="Competitive alert"
                        size={20}
                        style={{ color: "var(--infrared-coral)", marginTop: "2px" }}
                      />
                      <div className="flex-1">
                        <h4 className="mb-1" style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--text-sm)", color: "var(--infrared-coral)" }}>
                          {topRisingCompetitor.fullName}'s {topRisingCompetitor.change}% rise suggests category disruption
                        </h4>
                        <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)", lineHeight: "1.5" }}>
                          A competitor is gaining AI momentum faster than the market average. Consider reinforcing
                          differentiation and category positioning.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Card>
    </>
  );
}